import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ExtendedAccount } from "@common/types/reports/types";
import { FiltersTypes } from "@common/types/filters/general";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useEntitiesWithAccountingAvailable } from "@components/accounting/useEntitiesWithAccountingAvailable";
import { MergeChartOfAccountsModal } from "@components/accounting/chartOfAccounts/MergeChartOfAccountsModal";
import { RemoveOpeningBalanceModal } from "@components/accounting/chartOfAccounts/RemoveOpeningBalanceModal";
import { ReassignAccountRoleModal } from "@components/accounting/chartOfAccounts/ReassignAccountRoleModal";
import { ChartOfAccountsQueryData } from "@api/accountingReports.api";
import { ChartEditorWireOperation } from "@api/chartEditor.api";
import { useTemplateEditorDraft } from "../useTemplateEditorDraft";
import { useEditorDraftValidation } from "../useEditorDraftValidation";
import { ChartOfAccountsTemplateNestedEditor } from "../nested/ChartOfAccountsTemplateNestedEditor";
import { NestedEditorIsWritingContext } from "../nested/nestedEditorIsWritingContext";
import { NestedEditorRow } from "../nested/nestedRowTypes";
import { CoaEditorCommand } from "../nested/coaEditorCommand";
import { EditorFocusTarget, EditorReturnAction } from "../editorBodyTypes";
import {
    makeWorkspaceBuildRows,
    workspaceAllocateCodeText,
    workspaceEditorCodeRules,
} from "./workspaceEditorHost";
import { makeWorkspaceRowCapabilities } from "./makeWorkspaceRowCapabilities";
import {
    buildWorkspaceAccountsById,
    mapWorkspaceAccountsToDraft,
    mapWorkspaceGroupsToDraft,
} from "./workspaceDraftMapping";
import { translateEditorCommand } from "./translateEditorCommand";
import { runMergePreparation } from "./runMergePreparation";
import { buildRemoveOpeningBalanceModalProps } from "./buildRemoveOpeningBalanceModalProps";

const noop = (): void => {};

interface Props {
    data: ChartOfAccountsQueryData;
    // Already coerced (undefined filter → every business); never `?? []`.
    displayedEntityIds: number[];
    entityFilter: ReactNode;
    toolbarPrependAddon?: ReactNode;
    enqueue: (operation: ChartEditorWireOperation) => void;
    isEditingDisabled: boolean;
    disabledActionAddon?: ReactNode;
    islandAddon?: ReactNode;
    returnAction?: EditorReturnAction;
    isWriting: boolean;
    whenIdle: () => Promise<void>;
    getSessionToken: () => number;
    isSessionCurrent: (token: number) => boolean;
    isSessionBlocked: () => boolean;
    trackExternalWrite: (promise: Promise<unknown>) => void;
    resync: () => void;
    focusTarget?: EditorFocusTarget | null;
    onFocusTargetConsumed?: () => void;
    initialSearchFilter?: string;
    initialFilters?: FiltersTypes;
    onSearchFilterChange?: (search: string | undefined) => void;
    onAccountFiltersChange?: (
        filters: Pick<
            FiltersTypes,
            | "accountStatus"
            | "accountSource"
            | "accountRolePresence"
            | "accountRulePresence"
            | "coaTemplateGroupKeys"
        >,
    ) => void;
    onEntityFilterReset?: () => void;
}

export const WorkspaceChartEditorContent = ({
    data,
    displayedEntityIds,
    entityFilter,
    toolbarPrependAddon,
    enqueue,
    isEditingDisabled,
    disabledActionAddon,
    islandAddon,
    returnAction,
    isWriting,
    whenIdle,
    getSessionToken,
    isSessionCurrent,
    isSessionBlocked,
    trackExternalWrite,
    resync,
    focusTarget,
    onFocusTargetConsumed,
    initialSearchFilter,
    initialFilters,
    onSearchFilterChange,
    onAccountFiltersChange,
    onEntityFilterReset,
}: Props) => {
    const showAccountCodes = useShowAccountNumbers();
    const entities = useEntitiesWithAccountingAvailable();
    const queryClient = useQueryClient();
    const { error: errorToast } = useToaster();

    const [mergeAccount, setMergeAccount] = useState<ExtendedAccount | null>(
        null,
    );
    const [removeOpeningBalanceRow, setRemoveOpeningBalanceRow] =
        useState<NestedEditorRow | null>(null);
    const [reassignAccount, setReassignAccount] =
        useState<ExtendedAccount | null>(null);
    const [isPreparingMerge, setIsPreparingMerge] = useState(false);

    // The accounting-available list, not `useEntities({ onlyBusiness: true })`:
    // it is the same set the delete-scope sentence counts.
    const businessNamesFor = useCallback(
        (entityIds: number[]): string[] => {
            const wanted = new Set(entityIds);
            return entities
                .filter((entity) => wanted.has(entity.id))
                .map((entity) => entity.name ?? `Business ${entity.id}`);
        },
        [entities],
    );

    // A background refetch changes `data`, but the draft hook ignores later
    // prop changes, so local edits survive.
    const initialAccounts = useMemo(
        () => mapWorkspaceAccountsToDraft(data.accounts),
        [data.accounts],
    );
    const initialGroups = useMemo(
        () => mapWorkspaceGroupsToDraft(data.groups),
        [data.groups],
    );
    const accountsById = useMemo(
        () => buildWorkspaceAccountsById(data.accounts),
        [data.accounts],
    );

    const draft = useTemplateEditorDraft({
        accounts: initialAccounts,
        groups: initialGroups,
        allocateCodeText: workspaceAllocateCodeText,
    });

    // Every emitter mutates the draft and calls this second, so the closure
    // this render holds IS the pre-gesture draft the group paths must come
    // from. Translating later would read the renamed or deleted state.
    const draftAccounts = draft.accounts;
    const draftGroups = draft.groups;
    const onOperation = useCallback(
        (command: CoaEditorCommand) => {
            const operation = translateEditorCommand({
                command,
                draft: { accounts: draftAccounts, groups: draftGroups },
            });
            if (operation !== null) {
                enqueue(operation);
            }
        },
        [draftAccounts, draftGroups, enqueue],
    );

    const getRowCapabilities = useMemo(
        () => makeWorkspaceRowCapabilities({ accountsById }),
        [accountsById],
    );

    // A ref, not the click-event closure: the draft can change during whenIdle.
    const draftAccountsRef = useRef(draft.accounts);
    draftAccountsRef.current = draft.accounts;
    const getDraftAccount = useCallback(
        (id: string) =>
            draftAccountsRef.current.find((account) => account.id === id),
        [],
    );

    const handleMergeAccount = useCallback(
        async (row: NestedEditorRow) => {
            const result = await runMergePreparation({
                row,
                queryClient,
                accountsById,
                getDraftAccount,
                getSessionToken,
                isSessionCurrent,
                isSessionBlocked,
                whenIdle,
                setIsPreparingMerge,
                onError: errorToast,
            });
            if (result.kind === "prepared") {
                setMergeAccount(result.account);
                setIsPreparingMerge(false);
            }
        },
        [
            queryClient,
            accountsById,
            getDraftAccount,
            getSessionToken,
            isSessionCurrent,
            isSessionBlocked,
            whenIdle,
            errorToast,
        ],
    );

    const handleRemoveOpeningBalance = useCallback(
        async (row: NestedEditorRow) => {
            const seed = row.account;
            if (!seed) {
                return;
            }
            const token = getSessionToken();
            // A name-cell blur from the same pointer turn can enqueue a delete
            // before isWriting re-renders; wait it out, then re-check the row.
            await whenIdle();
            if (!isSessionCurrent(token) || isSessionBlocked()) {
                return;
            }
            if (!getDraftAccount(seed.id)) {
                return;
            }
            setRemoveOpeningBalanceRow(row);
        },
        [
            getSessionToken,
            whenIdle,
            isSessionCurrent,
            isSessionBlocked,
            getDraftAccount,
        ],
    );

    const handleReassignRole = useCallback(
        async (row: NestedEditorRow) => {
            const seed = row.account;
            if (!seed) {
                return;
            }
            const token = getSessionToken();
            await whenIdle();
            if (!isSessionCurrent(token) || isSessionBlocked()) {
                return;
            }
            // role lives on the server account, not the draft.
            const chartAccount = accountsById.get(seed.id);
            if (!chartAccount || !getDraftAccount(seed.id)) {
                return;
            }
            setReassignAccount(chartAccount);
        },
        [
            accountsById,
            getDraftAccount,
            getSessionToken,
            isSessionCurrent,
            isSessionBlocked,
            whenIdle,
        ],
    );

    const removeOpeningBalanceModalProps = useMemo(
        () =>
            removeOpeningBalanceRow
                ? buildRemoveOpeningBalanceModalProps({
                      row: removeOpeningBalanceRow,
                      getRowCapabilities,
                  })
                : null,
        [removeOpeningBalanceRow, getRowCapabilities],
    );

    // Hidden codes leave no cell for the issue bar to focus, so a duplicate
    // code must not raise an issue the accountant cannot navigate to or fix.
    const codeRules = useMemo(
        () =>
            showAccountCodes
                ? workspaceEditorCodeRules
                : {
                      ...workspaceEditorCodeRules,
                      findDuplicateCodeIds: () => new Set<string>(),
                  },
        [showAccountCodes],
    );

    const validation = useEditorDraftValidation({
        accounts: draft.accounts,
        groups: draft.groups,
        codeRules,
        getRowCapabilities,
    });

    const buildRows = useMemo(
        () => makeWorkspaceBuildRows(displayedEntityIds),
        [displayedEntityIds],
    );

    return (
        <NestedEditorIsWritingContext.Provider value={isWriting}>
            <ChartOfAccountsTemplateNestedEditor
                draft={draft}
                getRowCapabilities={getRowCapabilities}
                validation={{
                    invalidNameRowIds: validation.invalidNameRowIds,
                    invalidGroupNameIds: validation.invalidGroupNameIds,
                    duplicateGroupNameIds: validation.duplicateGroupNameIds,
                    duplicateNameIds: validation.duplicateNameIds,
                    duplicateCodeIds: validation.duplicateCodeIds,
                }}
                isSaveDisabled={false}
                saveBlockedReason={undefined}
                isSaving={false}
                saveHotkeyDescription=""
                onSave={noop}
                onSaveAndClose={noop}
                buildRows={buildRows}
                displayedEntityIds={displayedEntityIds}
                initialSearchFilter={initialSearchFilter}
                initialFilters={initialFilters}
                onSearchFilterChange={onSearchFilterChange}
                onAccountFiltersChange={onAccountFiltersChange}
                onEntityFilterReset={onEntityFilterReset}
                showSaveActions={false}
                showAccountCodes={showAccountCodes}
                emptyStateHeader="No accounts for the selected filters"
                accountNoun="chart"
                toolbarPrependAddon={toolbarPrependAddon}
                toolbarAddon={entityFilter}
                onOperation={onOperation}
                isEditingDisabled={isEditingDisabled || isPreparingMerge}
                disabledActionAddon={disabledActionAddon}
                islandAddon={islandAddon}
                returnAction={returnAction}
                businessNamesFor={businessNamesFor}
                onMergeAccount={handleMergeAccount}
                onRemoveOpeningBalance={handleRemoveOpeningBalance}
                onReassignRole={handleReassignRole}
                focusTarget={focusTarget}
                onFocusTargetConsumed={onFocusTargetConsumed}
            />
            {mergeAccount && (
                <MergeChartOfAccountsModal
                    show
                    onHide={() => setMergeAccount(null)}
                    account={mergeAccount}
                    onMerged={resync}
                    onMutation={trackExternalWrite}
                />
            )}
            {removeOpeningBalanceRow && removeOpeningBalanceModalProps && (
                <RemoveOpeningBalanceModal
                    show
                    onHide={() => setRemoveOpeningBalanceRow(null)}
                    accountName={removeOpeningBalanceModalProps.accountName}
                    targets={removeOpeningBalanceModalProps.targets}
                    rowEntityIds={removeOpeningBalanceModalProps.rowEntityIds}
                    onRemoved={resync}
                    onMutation={trackExternalWrite}
                />
            )}
            {reassignAccount && (
                <ReassignAccountRoleModal
                    show
                    onHide={() => setReassignAccount(null)}
                    account={reassignAccount}
                    onReassigned={resync}
                    onMutation={trackExternalWrite}
                />
            )}
        </NestedEditorIsWritingContext.Provider>
    );
};
